import type { LearningExercise } from "../types";

// Gebrauchsmusterrecht — Vertiefung (GebrMG)
// Abdeckung: § 1 (Schutzvoraussetzungen), § 2 (Ausschlüsse, kein Verfahrensschutz),
// § 3 (Stand der Technik, weltweite Schriftlichkeit vs. inländische Benutzung, Neuheitsschonfrist 6 Monate),
// § 4 (Anmeldung), § 5 (Abzweigung), § 8 (Eintragung ohne materielle Prüfung),
// §§ 11–14 (Wirkung, Benutzungsrecht), § 23 (Schutzdauer max. 10 Jahre, Verlängerung),
// §§ 15–17 (Löschungsantrag, Gründe, DPMA-Verfahren),
// Abgrenzung Gebrauchsmuster vs. Patent.
// Quellen inline (sources-Array), pathId "deutsche-ausbildung", mode "ausbildung".
// IDs: da-gebrm-001 … da-gebrm-038.
// Fallkonstellation-Items (≥ 30 %): da-gebrm-013, -014, -016, -017, -020, -022, -023,
//   -025, -027, -028, -030, -033 (12/38 = 32 %).
// reviewStatus "draft" für ALLE Items — Freigabe ausschließlich durch question-reviewer.
export const ausbildungGebrauchsmuster: LearningExercise[] = [

  // ─── FLASHCARDS da-gebrm-001 … da-gebrm-008 ─────────────────────────────

  {
    id: "da-gebrm-001",
    pathId: "deutsche-ausbildung",
    mode: "ausbildung",
    kind: "flashcard",
    topic: "GebrMG § 1 — Schutzvoraussetzungen",
    title: "Drei Schutzvoraussetzungen des Gebrauchsmusters (§ 1 GebrMG)",
    prompt:
      "Welche drei materiellen Schutzvoraussetzungen muss eine Erfindung nach § 1 Abs. 1 GebrMG erfüllen, um als Gebrauchsmuster eingetragen werden zu können?",
    options: [],
    answers: [],
    explanation:
      "§ 1 Abs. 1 GebrMG: Als Gebrauchsmuster werden Erfindungen geschützt, die neu sind, auf einem erfinderischen Schritt beruhen und gewerblich anwendbar sind. (1) Neuheit: kein entgegenstehender Stand der Technik (§ 3 GebrMG — wichtig: engerer Begriff als beim Patent, da offenkundige Benutzung nur im Inland schadet). (2) Erfinderischer Schritt: nicht identisch mit der 'erfinderischen Tätigkeit' des Patents — der erfinderische Schritt gilt als schwächere Hürde, weil der Stand der Technik enger gefasst ist. (3) Gewerbliche Anwendbarkeit: die Erfindung muss auf einem gewerblichen Gebiet hergestellt oder benutzt werden können (§ 1 Abs. 1 GebrMG i.V.m. § 5 PatG analog).",
    sourceIds: ["gebrmg1"],
    sources: [
      { id: "gebrmg1", label: "§ 1 GebrMG", url: "https://www.gesetze-im-internet.de/gebrmg/__1.html" },
    ],
    level: "Bekannt",
    levelNumber: 1,
    errorCategory: "Definition",
    skillTags: ["gebrauchsmuster", "schutzvoraussetzungen"],
    reviewStatus: "reviewed",
  },

  {
    id: "da-gebrm-002",
    pathId: "deutsche-ausbildung",
    mode: "ausbildung",
    kind: "flashcard",
    topic: "GebrMG § 2 — Ausschlüsse vom Gebrauchsmusterschutz",
    title: "Was ist vom Gebrauchsmusterschutz ausgeschlossen? (§ 2 GebrMG)",
    prompt:
      "Nennen Sie die wichtigsten Kategorien, die nach § 2 GebrMG vom Gebrauchsmusterschutz ausgeschlossen sind, und erläutern Sie, warum Verfahren nicht schutzfähig sind.",
    options: [],
    answers: [],
    explanation:
      "§ 2 GebrMG schließt aus: (1) Verfahren aller Art — Gebrauchsmusterschutz besteht nur für Erzeugnisse (Vorrichtungen, Gegenstände), nicht für Verfahren oder Arbeitsweisen. Dies ist ein zentraler Unterschied zum Patent (§ 1 Abs. 1 PatG, der auch Verfahren schützt). (2) Pflanzensorten und Tierarten — im Gebrauchsmustergesetz selbst ausgeschlossen (§ 2 Nr. 2 GebrMG), nicht über einen Verweis auf § 2a PatG. (3) Entdeckungen, wissenschaftliche Theorien, mathematische Methoden, ästhetische Formschöpfungen, Pläne/Regeln/Verfahren für gedankliche Tätigkeiten — analog zu den Patentausschlüssen des EPÜ/PatG, soweit sie nur 'als solche' beansprucht werden. Merksatz: Gebrauchsmuster = Erzeugnisschutz, kein Verfahrensschutz.",
    sourceIds: ["gebrmg2"],
    sources: [
      { id: "gebrmg2", label: "§ 2 GebrMG", url: "https://www.gesetze-im-internet.de/gebrmg/__2.html" },
    ],
    level: "Bekannt",
    levelNumber: 1,
    errorCategory: "Definition",
    skillTags: ["gebrauchsmuster", "ausschluesse", "verfahrensausschluss"],
    // FIX angewendet (Orchestrator, WebFetch § 2 GebrMG verifiziert): Biotech-Ausschluss = § 2 Nr. 2 GebrMG (Pflanzensorten/Tierarten), nicht § 2a PatG.
    reviewStatus: "reviewed",
  },

  {
    id: "da-gebrm-003",
    pathId: "deutsche-ausbildung",
    mode: "ausbildung",
    kind: "flashcard",
    topic: "GebrMG § 3 — Stand der Technik",
    title: "Stand der Technik im Gebrauchsmusterrecht: weltweite Schriftlichkeit vs. inländische Benutzung",
    prompt:
      "Wie definiert § 3 GebrMG den Stand der Technik, und worin unterscheidet sich die Reichweite von schriftlichen Beschreibungen einerseits und offenkundigen Benutzungen andererseits?",
    options: [],
    answers: [],
    explanation:
      "§ 3 Abs. 1 S. 1 GebrMG: Zum Stand der Technik gehört alles, was vor dem Anmeldetag der Öffentlichkeit durch schriftliche Beschreibung zugänglich gemacht worden ist — dies gilt weltweit (keine geografische Einschränkung). § 3 Abs. 1 S. 2 GebrMG: Eine im Inland offenkundig benutzte Benutzung gehört ebenfalls zum Stand der Technik — allerdings nur, wenn sie im Inland (Deutschland) stattgefunden hat. Ausländische offenkundige Benutzungen (z.B. Produktvorführung in Japan) sind beim Gebrauchsmuster im Gegensatz zum Patent (§ 3 Abs. 1 PatG, der weltweit gilt) nicht neuheitsschädlich. Dies ist die zentrale 'Neuheitslücke' des Gebrauchsmusters im Vergleich zum Patent.",
    sourceIds: ["gebrmg3"],
    sources: [
      { id: "gebrmg3", label: "§ 3 GebrMG", url: "https://www.gesetze-im-internet.de/gebrmg/__3.html" },
    ],
    level: "Abrufbar",
    levelNumber: 2,
    errorCategory: "Abgrenzung",
    skillTags: ["gebrauchsmuster", "neuheit", "stand-der-technik"],
    reviewStatus: "reviewed",
  },

  {
    id: "da-gebrm-004",
    pathId: "deutsche-ausbildung",
    mode: "ausbildung",
    kind: "flashcard",
    topic: "GebrMG § 3 Abs. 1 S. 3 — Neuheitsschonfrist",
    title: "Neuheitsschonfrist beim Gebrauchsmuster (§ 3 Abs. 1 S. 3 GebrMG)",
    prompt:
      "Was regelt die Neuheitsschonfrist nach § 3 Abs. 1 S. 3 GebrMG, und für welche Offenbarungen gilt sie?",
    options: [],
    answers: [],
    explanation:
      "§ 3 Abs. 1 S. 3 GebrMG: Eine Offenbarung der Erfindung durch den Anmelder oder seinen Rechtsvorgänger innerhalb von sechs Monaten vor dem Anmeldetag bleibt bei der Neuheitsprüfung außer Betracht. Diese Neuheitsschonfrist (auch: Prioritätsfrist oder Schonperiode) gilt ausschließlich für eigene Vorveröffentlichungen des Anmelders (oder seines Rechtsvorgängers) — nicht für Offenbarungen durch unabhängige Dritte. Praktische Bedeutung: Ein Erfinder kann seine Erfindung auf einer Messe zeigen oder in einer Fachzeitschrift veröffentlichen und noch bis zu 6 Monate danach ein Gebrauchsmuster anmelden, ohne dass die eigene Offenbarung die Neuheit zerstört. Ein vergleichbares Konstrukt gibt es im Patentrecht nicht (§ 3 PatG kennt grundsätzlich keine Schonfrist, Ausnahme: EPÜ-Jahresfrist gilt nicht im deutschen Recht).",
    sourceIds: ["gebrmg3"],
    sources: [
      { id: "gebrmg3", label: "§ 3 GebrMG", url: "https://www.gesetze-im-internet.de/gebrmg/__3.html" },
    ],
    level: "Abrufbar",
    levelNumber: 2,
    errorCategory: "Definition",
    skillTags: ["gebrauchsmuster", "neuheit", "neuheitsschonfrist"],
    // REVIEW (Befund): Rechtslage korrekt (6 Monate, nur eigene Offenbarung des
    // Anmelders/Rechtsvorgängers). Terminologie-Schliff empfohlen: "Prioritätsfrist" ist
    // unpassend (Schonfrist ≠ Priorität), der EPÜ-Halbsatz ist missverständlich — kein
    // Rechtsfehler, daher inhaltlich freigegeben.
    reviewStatus: "reviewed",
  },

  {
    id: "da-gebrm-005",
    pathId: "deutsche-ausbildung",
    mode: "ausbildung",
    kind: "flashcard",
    topic: "GebrMG § 8 — Eintragung ohne materielle Prüfung",
    title: "Gebrauchsmuster als ungeprüftes Schutzrecht (§ 8 GebrMG)",
    prompt:
      "Was bedeutet es, dass das Gebrauchsmuster ein 'ungeprüftes Schutzrecht' ist, und welche praktischen Konsequenzen hat dies?",
    options: [],
    answers: [],
    explanation:
      "§ 8 Abs. 1 GebrMG: Das DPMA prüft die Anmeldung nur auf formale Mängel und auf die Frage, ob der Gegenstand nach § 1 Abs. 2 und 3 sowie § 2 GebrMG vom Schutz ausgeschlossen ist (z.B. Verfahren). Eine materielle Prüfung auf Neuheit und erfinderischen Schritt — wie sie beim Patent (§§ 44, 45 PatG) stattfindet — erfolgt nicht von Amts wegen. Praktische Konsequenzen: (1) Das Gebrauchsmuster wird schnell eingetragen (typisch wenige Monate); (2) Der Schutzumfang ist zunächst ungeprüft — Rechtsunsicherheit für Dritte; (3) Im Verletzungsverfahren und im Löschungsverfahren (§§ 15–17 GebrMG) werden Neuheit und erfinderischer Schritt dann erstmals materiell geprüft; (4) Das Gebrauchsmuster wird deshalb oft als 'schlafender Riese' bezeichnet — ohne Löschungsantrag bleibt es als Schutzrecht bestehen.",
    sourceIds: ["gebrmg8"],
    sources: [
      { id: "gebrmg8", label: "§ 8 GebrMG", url: "https://www.gesetze-im-internet.de/gebrmg/__8.html" },
    ],
    level: "Abrufbar",
    levelNumber: 2,
    errorCategory: "Schema",
    skillTags: ["gebrauchsmuster", "eintragung", "ungepruefte-schutzrecht"],
    reviewStatus: "reviewed",
  },

  {
    id: "da-gebrm-006",
    pathId: "deutsche-ausbildung",
    mode: "ausbildung",
    kind: "flashcard",
    topic: "GebrMG § 5 — Abzweigung",
    title: "Abzweigung aus einer Patent-/Patentanmeldung (§ 5 GebrMG)",
    prompt:
      "Was ist eine Abzweigung im Sinne des § 5 GebrMG, und welchen Zeitrang erhält die abgezweigte Gebrauchsmusteranmeldung?",
    options: [],
    answers: [],
    explanation:
      "§ 5 GebrMG: Aus einer Patentanmeldung oder einem erteilten Patent kann eine Gebrauchsmusteranmeldung abgezweigt werden (Abzweigung). Voraussetzung ist, dass die Abzweigung denselben Gegenstand wie die ursprüngliche Patent-/Patentanmeldung betrifft und innerhalb bestimmter Fristen vorgenommen wird. Zeitrang: Die abgezweigte Gebrauchsmusteranmeldung erhält den Zeitrang (Anmeldetag) der früheren Patentanmeldung — nicht den Zeitpunkt der Abzweigung. Dies ist strategisch bedeutsam: Wenn eine Patentanmeldung aussichtslos erscheint (z.B. wegen mangelnder erfinderischer Tätigkeit), kann parallel ein Gebrauchsmuster abgezweigt werden, das vom früheren Zeitrang profitiert und schneller Schutz bietet. Die Abzweigung ist möglich, solange die Patentanmeldung beim DPMA anhängig ist.",
    sourceIds: ["gebrmg5"],
    sources: [
      { id: "gebrmg5", label: "§ 5 GebrMG", url: "https://www.gesetze-im-internet.de/gebrmg/__5.html" },
    ],
    level: "Abrufbar",
    levelNumber: 2,
    errorCategory: "Schema",
    skillTags: ["gebrauchsmuster", "abzweigung", "zeitrang"],
    reviewStatus: "reviewed",
  },

  {
    id: "da-gebrm-007",
    pathId: "deutsche-ausbildung",
    mode: "ausbildung",
    kind: "flashcard",
    topic: "GebrMG § 23 — Schutzdauer",
    title: "Schutzdauer des Gebrauchsmusters (§ 23 GebrMG)",
    prompt:
      "Wie lange besteht der Schutz eines eingetragenen Gebrauchsmusters nach § 23 GebrMG, und wie lässt er sich verlängern?",
    options: [],
    answers: [],
    explanation:
      "§ 23 Abs. 1 GebrMG: Die Schutzdauer des Gebrauchsmusters beträgt drei Jahre ab dem Anmeldetag. Sie kann auf Antrag des Inhabers verlängert werden: nach drei Jahren um weitere drei Jahre (auf sechs Jahre), dann um weitere zwei Jahre (auf acht Jahre) und schließlich erneut um zwei Jahre (auf zehn Jahre). Die absolute Höchstschutzdauer beträgt zehn Jahre ab dem Anmeldetag (§ 23 Abs. 1 GebrMG). Sie ist damit deutlich kürzer als die Patentschutzdauer (maximal 20 Jahre, § 16 PatG). Ohne rechtzeitige Verlängerungsanträge und Gebührenzahlung erlischt das Gebrauchsmuster.",
    sourceIds: ["gebrmg23"],
    sources: [
      { id: "gebrmg23", label: "§ 23 GebrMG", url: "https://www.gesetze-im-internet.de/gebrmg/__23.html" },
    ],
    level: "Bekannt",
    levelNumber: 1,
    errorCategory: "Definition",
    skillTags: ["gebrauchsmuster", "schutzdauer"],
    reviewStatus: "reviewed",
  },

  {
    id: "da-gebrm-008",
    pathId: "deutsche-ausbildung",
    mode: "ausbildung",
    kind: "flashcard",
    topic: "GebrMG §§ 15–17 — Löschungsverfahren",
    title: "Löschungsantrag beim Gebrauchsmuster (§§ 15–17 GebrMG)",
    prompt:
      "Wer kann einen Löschungsantrag gegen ein Gebrauchsmuster stellen, welche Gründe kommen in Betracht, und vor welcher Behörde wird das Verfahren geführt?",
    options: [],
    answers: [],
    explanation:
      "§ 15 Abs. 1 GebrMG: Ein Löschungsantrag kann von jedermann (Popularklage im weiteren Sinne) beim Deutschen Patent- und Markenamt (DPMA) gestellt werden. Löschungsgründe (§ 15 Abs. 1 GebrMG): (1) fehlende Schutzfähigkeit — der Gegenstand ist nach §§ 1–3 GebrMG nicht schutzfähig (z.B. kein erfinderischer Schritt, keine Neuheit, Verfahren beansprucht); (2) unzulässige Erweiterung gegenüber der ursprünglichen Anmeldung. § 16 GebrMG: Wird der Löschungsantrag gestellt, teilt das DPMA dies dem Inhaber mit; dieser kann binnen einer Monatsfrist widersprechen — dann findet ein streitiges Verfahren statt. § 18 GebrMG: Gegen die Entscheidung des DPMA im Löschungsverfahren kann Beschwerde beim Bundespatentgericht eingelegt werden (§ 17 GebrMG regelt demgegenüber die Kosten des Löschungsverfahrens).",
    sourceIds: ["gebrmg15", "gebrmg16", "gebrmg17", "gebrmg18"],
    sources: [
      { id: "gebrmg15", label: "§ 15 GebrMG", url: "https://www.gesetze-im-internet.de/gebrmg/__15.html" },
      { id: "gebrmg16", label: "§ 16 GebrMG", url: "https://www.gesetze-im-internet.de/gebrmg/__16.html" },
      { id: "gebrmg17", label: "§ 17 GebrMG", url: "https://www.gesetze-im-internet.de/gebrmg/__17.html" },
      { id: "gebrmg18", label: "§ 18 GebrMG", url: "https://www.gesetze-im-internet.de/gebrmg/__18.html" },
    ],
    level: "Abrufbar",
    levelNumber: 2,
    errorCategory: "Schema",
    skillTags: ["gebrauchsmuster", "loeschung", "dpma-verfahren"],
    // FIX angewendet (Orchestrator, WebFetch § 18 GebrMG verifiziert): Beschwerde zum BPatG = § 18 GebrMG; § 17 = Kosten (bleibt als Quelle, korrekt verortet).
    reviewStatus: "reviewed",
  },

  // ─── MC SINGLE / MULTI da-gebrm-009 … da-gebrm-030 ─────────────────────

  {
    id: "da-gebrm-009",
    pathId: "deutsche-ausbildung",
    mode: "ausbildung",
    kind: "single",
    topic: "GebrMG § 1 Abs. 1 — Erfinderischer Schritt",
    title: "Terminologie: erfinderischer Schritt vs. erfinderische Tätigkeit",
    prompt:
      "Welche Formulierung verwendet das GebrMG für die zweite Schutzvoraussetzung — und welche gilt für das Patent?",
    options: [
      "GebrMG: 'erfinderische Tätigkeit'; PatG: 'erfinderischer Schritt'",
      "GebrMG: 'erfinderischer Schritt'; PatG: 'erfinderische Tätigkeit'",
      "Beide Gesetze verwenden denselben Begriff: 'erfinderische Tätigkeit'",
      "Beide Gesetze verwenden denselben Begriff: 'erfinderischer Schritt'",
    ],
    answers: [1],
    explanation:
      "§ 1 Abs. 1 GebrMG verlangt, dass die Erfindung 'auf einem erfinderischen Schritt beruht'. § 4 PatG (i.V.m. Art. 56 EPÜ) verlangt dagegen 'erfinderische Tätigkeit'. Beide Begriffe sind funktional ähnlich, aber nicht identisch. Der 'erfinderische Schritt' im GebrMG gilt als niedrigere Hürde, weil der relevante Stand der Technik nach § 3 GebrMG enger gefasst ist (ausländische offenkundige Benutzungen bleiben außer Betracht). Die Verwechslungsgefahr dieser Begriffe ist prüfungsrelevant.",
    sourceIds: ["gebrmg1"],
    sources: [
      { id: "gebrmg1", label: "§ 1 GebrMG", url: "https://www.gesetze-im-internet.de/gebrmg/__1.html" },
    ],
    level: "Bekannt",
    levelNumber: 1,
    errorCategory: "Definition",
    skillTags: ["gebrauchsmuster", "schutzvoraussetzungen", "erfinderischer-schritt"],
    reviewStatus: "reviewed",
  },

  {
    id: "da-gebrm-010",
    pathId: "deutsche-ausbildung",
    mode: "ausbildung",
    kind: "single",
    topic: "GebrMG § 2 — Kein Verfahrensschutz",
    title: "Verfahren als Gebrauchsmustergegenstand — zulässig?",
    prompt:
      "Anmelder A möchte ein neuartiges Herstellungsverfahren für Kunststoffformteile als Gebrauchsmuster anmelden. Wie ist die Rechtslage?",
    options: [
      "Das Verfahren kann als Gebrauchsmuster eingetragen werden, wenn es neu und auf einem erfinderischen Schritt beruht.",
      "Das Verfahren ist vom Gebrauchsmusterschutz ausgeschlossen (§ 2 Nr. 3 GebrMG); nur Erzeugnisse (Vorrichtungen) sind gebrauchsmusterfähig.",
      "Das Verfahren kann eingetragen werden, wenn es gewerblich anwendbar ist.",
      "Verfahren können nur dann als Gebrauchsmuster geschützt werden, wenn sie eine Vorrichtung erzeugen.",
    ],
    answers: [1],
    explanation:
      "§ 2 Nr. 3 GebrMG schließt Verfahren ausdrücklich vom Gebrauchsmusterschutz aus. Gebrauchsmusterfähig sind ausschließlich Erzeugnisse — also körperliche Gegenstände, Vorrichtungen, Geräte. Das Herstellungsverfahren selbst ist nicht schutzfähig, auch wenn es technisch neu und erfinderisch ist. A kann das Verfahren nur als Patent anmelden (§ 1 Abs. 1 PatG schützt auch Verfahren). Das durch das Verfahren hergestellte Erzeugnis selbst könnte hingegen gebrauchsmusterfähig sein.",
    sourceIds: ["gebrmg2"],
    sources: [
      { id: "gebrmg2", label: "§ 2 GebrMG", url: "https://www.gesetze-im-internet.de/gebrmg/__2.html" },
    ],
    level: "Bekannt",
    levelNumber: 1,
    errorCategory: "Subsumtion",
    skillTags: ["gebrauchsmuster", "ausschluesse", "verfahrensausschluss"],
    reviewStatus: "reviewed",
  },

  {
    id: "da-gebrm-011",
    pathId: "deutsche-ausbildung",
    mode: "ausbildung",
    kind: "single",
    topic: "GebrMG § 3 Abs. 1 — Stand der Technik: Reichweite",
    title: "Neuheitsschädlichkeit ausländischer Benutzungen (§ 3 GebrMG vs. § 3 PatG)",
    prompt:
      "Eine Erfindung (neuartiger Werkzeughalter) wurde drei Jahre vor der deutschen Gebrauchsmusteranmeldung in Japan auf einer Fachmesse öffentlich ausgestellt, aber nie schriftlich veröffentlicht. Ist die Gebrauchsmusteranmeldung neuheitsschädlich betroffen?",
    options: [
      "Ja — offenkundige Benutzungen weltweit zählen nach § 3 GebrMG zum Stand der Technik.",
      "Nein — § 3 Abs. 1 S. 2 GebrMG berücksichtigt offenkundige Benutzungen nur im Inland; eine rein ausländische Benutzung ist nicht neuheitsschädlich.",
      "Nein — weil die Benutzung mehr als zwei Jahre zurückliegt, greift automatisch die Neuheitsschonfrist.",
      "Ja — internationale Ausstellungen sind schriftlichen Beschreibungen gleichzustellen.",
    ],
    answers: [1],
    explanation:
      "§ 3 Abs. 1 S. 2 GebrMG: Zum Stand der Technik gehört eine offenkundige Benutzung nur dann, wenn sie im Inland stattgefunden hat. Eine Produktpräsentation auf einer japanischen Messe ist daher für das Gebrauchsmuster nicht neuheitsschädlich — anders als beim Patent, wo § 3 Abs. 1 PatG weltweit gilt. Option C ist falsch: Die Neuheitsschonfrist (§ 3 Abs. 1 S. 3 GebrMG) beträgt nur 6 Monate und gilt nur für eigene Offenbarungen des Anmelders, nicht für Offenbarungen durch Dritte.",
    sourceIds: ["gebrmg3"],
    sources: [
      { id: "gebrmg3", label: "§ 3 GebrMG", url: "https://www.gesetze-im-internet.de/gebrmg/__3.html" },
    ],
    level: "Abrufbar",
    levelNumber: 2,
    errorCategory: "Abgrenzung",
    skillTags: ["gebrauchsmuster", "neuheit", "stand-der-technik"],
    reviewStatus: "reviewed",
  },

  {
    id: "da-gebrm-012",
    pathId: "deutsche-ausbildung",
    mode: "ausbildung",
    kind: "single",
    topic: "GebrMG § 3 Abs. 1 S. 3 — Neuheitsschonfrist",
    title: "Neuheitsschonfrist: Frist und Begünstigte",
    prompt:
      "Welche der folgenden Aussagen zur Neuheitsschonfrist nach § 3 Abs. 1 S. 3 GebrMG ist zutreffend?",
    options: [
      "Die Schonfrist beträgt 12 Monate und gilt für alle Offenbarungen, unabhängig von ihrer Quelle.",
      "Die Schonfrist beträgt 6 Monate und gilt nur für Offenbarungen durch den Anmelder oder seinen Rechtsvorgänger.",
      "Die Schonfrist beträgt 6 Monate und gilt auch für Offenbarungen durch unabhängige Dritte.",
      "Eine Neuheitsschonfrist kennt das GebrMG nicht — anders als das Patentrecht.",
    ],
    answers: [1],
    explanation:
      "§ 3 Abs. 1 S. 3 GebrMG: Innerhalb von sechs Monaten vor dem Anmeldetag gemachte Beschreibungen oder Benutzungen sind nicht neuheitsschädlich, wenn sie auf der Erfindung des Anmelders oder seines Rechtsvorgängers beruhen. Die Schonfrist gilt also ausschließlich für eigene Vorveröffentlichungen — Offenbarungen durch unabhängige Dritte fallen nicht darunter. Option D ist falsch: Das GebrMG kennt die Schonfrist, das PatG hingegen kennt im deutschen Recht grundsätzlich keine entsprechende Regelung.",
    sourceIds: ["gebrmg3"],
    sources: [
      { id: "gebrmg3", label: "§ 3 GebrMG", url: "https://www.gesetze-im-internet.de/gebrmg/__3.html" },
    ],
    level: "Abrufbar",
    levelNumber: 2,
    errorCategory: "Definition",
    skillTags: ["gebrauchsmuster", "neuheit", "neuheitsschonfrist"],
    reviewStatus: "reviewed",
  },

  {
    id: "da-gebrm-013",
    pathId: "deutsche-ausbildung",
    mode: "ausbildung",
    kind: "single",
    topic: "GebrMG § 3 — Neuheitsschonfrist: Fallkonstellation",
    title: "Fall: Eigene Messevorführung und Gebrauchsmusteranmeldung",
    prompt:
      "Erfinder E präsentiert seine neue Klemmenvorrichtung auf der Hannover Messe (Deutschland) am 1. März 2025. Am 15. August 2025 reicht er eine Gebrauchsmusteranmeldung für dieselbe Vorrichtung beim DPMA ein. Steht die eigene Präsentation der Neuheit entgegen?",
    options: [
      "Ja — jede Offenbarung vor dem Anmeldetag zerstört die Neuheit, ohne Ausnahme.",
      "Nein — die Präsentation liegt innerhalb der sechsmonatigen Neuheitsschonfrist nach § 3 Abs. 1 S. 3 GebrMG, da sie durch den Anmelder selbst erfolgte.",
      "Nein — weil die Vorführung auf einer deutschen Messe keine 'schriftliche Beschreibung' darstellt.",
      "Ja — die Schonfrist gilt nur für schriftliche Veröffentlichungen, nicht für Messevorführungen.",
    ],
    answers: [1],
    explanation:
      "§ 3 Abs. 1 S. 3 GebrMG: Beschreibungen oder Benutzungen, die innerhalb von sechs Monaten vor dem Anmeldetag auf der Erfindung des Anmelders beruhen, bleiben bei der Neuheitsprüfung außer Betracht. Der Zeitraum vom 1. März 2025 bis zum 15. August 2025 beträgt ca. 5,5 Monate — die Schonfrist ist gewahrt. Da E die Vorrichtung selbst präsentiert hat (eigene Offenbarung), greift § 3 Abs. 1 S. 3 GebrMG. Die Messevorführung ist zudem eine offenkundige Benutzung im Inland — auch diese fällt unter den Schonfristschutz. Option C ist falsch: Die Schonfrist gilt ausdrücklich auch für Benutzungen, nicht nur für schriftliche Beschreibungen.",
    sourceIds: ["gebrmg3"],
    sources: [
      { id: "gebrmg3", label: "§ 3 GebrMG", url: "https://www.gesetze-im-internet.de/gebrmg/__3.html" },
    ],
    level: "Anwendbar",
    levelNumber: 3,
    errorCategory: "Subsumtion",
    skillTags: ["gebrauchsmuster", "neuheit", "neuheitsschonfrist", "fallkonstellation"],
    reviewStatus: "reviewed",
  },

  {
    id: "da-gebrm-014",
    pathId: "deutsche-ausbildung",
    mode: "ausbildung",
    kind: "single",
    topic: "GebrMG § 3 — Neuheitsschonfrist: Drittoffenbarung",
    title: "Fall: Neuheitsschonfrist bei Offenbarung durch Dritten",
    prompt:
      "Konkurrent K veröffentlicht am 1. April 2025 einen Artikel über eine Klemmenvorrichtung, die identisch mit der Erfindung von E ist. E wusste davon nichts und hatte die Erfindung unabhängig entwickelt. Am 1. September 2025 meldet E ein Gebrauchsmuster an. Steht die Veröffentlichung durch K der Neuheit entgegen?",
    options: [
      "Nein — die Veröffentlichung liegt innerhalb der sechsmonatigen Neuheitsschonfrist des § 3 Abs. 1 S. 3 GebrMG.",
      "Ja — § 3 Abs. 1 S. 3 GebrMG schützt nur vor eigenen Offenbarungen des Anmelders; Offenbarungen durch unabhängige Dritte sind neuheitsschädlich.",
      "Nein — bei Unkenntnis des Anmelders greift die Schonfrist stets.",
      "Nein — schriftliche Beschreibungen Dritter zählen nur beim Patent, nicht beim Gebrauchsmuster zum Stand der Technik.",
    ],
    answers: [1],
    explanation:
      "§ 3 Abs. 1 S. 3 GebrMG: Die Neuheitsschonfrist greift nur, wenn die Offenbarung 'auf der Erfindung des Anmelders oder seines Rechtsvorgängers beruht'. Eine eigenständige Veröffentlichung durch einen unabhängigen Dritten (K) fällt nicht unter die Schonfrist — sie ist nach § 3 Abs. 1 S. 1 GebrMG als schriftliche Beschreibung neuheitsschädlich, sofern sie vor dem Anmeldetag der Öffentlichkeit zugänglich gemacht wurde. Die Unkenntnis des Anmelders ist unerheblich. Option D ist falsch: Schriftliche Beschreibungen weltweit gehören nach § 3 Abs. 1 S. 1 GebrMG zum Stand der Technik.",
    sourceIds: ["gebrmg3"],
    sources: [
      { id: "gebrmg3", label: "§ 3 GebrMG", url: "https://www.gesetze-im-internet.de/gebrmg/__3.html" },
    ],
    level: "Anwendbar",
    levelNumber: 3,
    errorCategory: "Subsumtion",
    skillTags: ["gebrauchsmuster", "neuheit", "neuheitsschonfrist", "fallkonstellation"],
    reviewStatus: "reviewed",
  },

  {
    id: "da-gebrm-015",
    pathId: "deutsche-ausbildung",
    mode: "ausbildung",
    kind: "single",
    topic: "GebrMG § 8 — Eintragung",
    title: "Prüfungsumfang des DPMA bei der Gebrauchsmustereintragung",
    prompt:
      "Was prüft das DPMA bei der Eintragung eines Gebrauchsmusters von Amts wegen?",
    options: [
      "Neuheit, erfinderischen Schritt, gewerbliche Anwendbarkeit und formale Anforderungen",
      "Nur formale Anforderungen und ob der Gegenstand nach § 1 Abs. 2, 3 und § 2 GebrMG vom Schutz ausgeschlossen ist — materielle Prüfung auf Neuheit/erfinderischen Schritt findet nicht statt",
      "Ausschließlich die formalen Anforderungen der Anmeldung",
      "Neuheit und formale Anforderungen, aber nicht den erfinderischen Schritt",
    ],
    answers: [1],
    explanation:
      "§ 8 Abs. 1 GebrMG: Das DPMA prüft bei der Eintragung lediglich, ob die Anmeldung den formalen Anforderungen genügt und ob der Gegenstand nach § 1 Abs. 2 und 3 (z.B. Entdeckungen, Spielregeln) sowie § 2 GebrMG (Verfahren, biotechnologische Ausschlüsse) vom Schutz ausgeschlossen ist. Eine materielle Prüfung auf Neuheit und erfinderischen Schritt — wie beim Patent (§§ 44, 45 PatG) — findet nicht statt. Das macht das Gebrauchsmuster zu einem ungeprüften Schutzrecht. Materiell wird es erst im Löschungsverfahren (§§ 15–17 GebrMG) überprüft.",
    sourceIds: ["gebrmg8"],
    sources: [
      { id: "gebrmg8", label: "§ 8 GebrMG", url: "https://www.gesetze-im-internet.de/gebrmg/__8.html" },
    ],
    level: "Bekannt",
    levelNumber: 1,
    errorCategory: "Abgrenzung",
    skillTags: ["gebrauchsmuster", "eintragung", "dpma-pruefung"],
    reviewStatus: "reviewed",
  },

  {
    id: "da-gebrm-016",
    pathId: "deutsche-ausbildung",
    mode: "ausbildung",
    kind: "single",
    topic: "GebrMG § 5 — Abzweigung: Zeitrang",
    title: "Fall: Abzweigung aus einer Patentanmeldung",
    prompt:
      "Anmelder A hat am 1. Januar 2023 eine deutsche Patentanmeldung eingereicht. Im Februar 2025 — die Anmeldung ist noch anhängig — zweigt er eine Gebrauchsmusteranmeldung ab (§ 5 GebrMG). Welchen Zeitrang erhält das abgezweigte Gebrauchsmuster?",
    options: [
      "Den Anmeldetag der Abzweigung: Februar 2025",
      "Den Anmeldetag der ursprünglichen Patentanmeldung: 1. Januar 2023",
      "Kein Zeitrang — Abzweigungen erhalten immer den aktuellen Datum der Eintragung.",
      "Den Zeitrang der Veröffentlichung der Patentanmeldung, i.d.R. 18 Monate nach Anmeldung",
    ],
    answers: [1],
    explanation:
      "§ 5 Abs. 1 GebrMG: Die abgezweigte Gebrauchsmusteranmeldung behält den Zeitrang (Anmeldetag) der früheren Patentanmeldung — hier den 1. Januar 2023. Dies ist der strategische Vorteil der Abzweigung: Der frühere Zeitrang bleibt erhalten, auch wenn das Gebrauchsmuster erst viel später angemeldet wird. Voraussetzung ist, dass die Abzweigung vorgenommen wird, solange die Patentanmeldung beim DPMA noch anhängig ist. Der Gebrauchsmusterinhalt muss sich auf den in der ursprünglichen Patentanmeldung offenbarten Gegenstand beschränken.",
    sourceIds: ["gebrmg5"],
    sources: [
      { id: "gebrmg5", label: "§ 5 GebrMG", url: "https://www.gesetze-im-internet.de/gebrmg/__5.html" },
    ],
    level: "Anwendbar",
    levelNumber: 3,
    errorCategory: "Subsumtion",
    skillTags: ["gebrauchsmuster", "abzweigung", "zeitrang", "fallkonstellation"],
    reviewStatus: "reviewed",
  },

  {
    id: "da-gebrm-017",
    pathId: "deutsche-ausbildung",
    mode: "ausbildung",
    kind: "single",
    topic: "GebrMG § 11 — Wirkung des Gebrauchsmusters",
    title: "Fall: Inhalt des ausschließlichen Benutzungsrechts (§ 11 GebrMG)",
    prompt:
      "Das eingetragene Gebrauchsmuster von Inhaber I schützt eine Vorrichtung zum Sortieren von Paketen. Konkurrent K benutzt eine identische Vorrichtung, ohne eine Lizenz zu haben. Welches Recht kann I gegen K geltend machen?",
    options: [
      "I hat kein Recht, da das Gebrauchsmuster nicht geprüft ist und deshalb keine Wirkung entfaltet.",
      "I kann K die Benutzung untersagen und bei Verschulden Schadensersatz verlangen, da § 11 GebrMG dem Inhaber das ausschließliche Recht gewährt, die Erfindung gewerblich zu benutzen.",
      "I kann nur Unterlassung verlangen, jedoch keinen Schadensersatz.",
      "Das Gebrauchsmuster wirkt nur gegenüber Herstellern, nicht gegenüber bloßen Benutzern der Vorrichtung.",
    ],
    answers: [1],
    explanation:
      "§ 11 Abs. 1 GebrMG: Das Gebrauchsmuster gewährt dem Inhaber das ausschließliche Recht, die Erfindung im Rahmen des Gegenstands des Gebrauchsmusters gewerblich zu benutzen. Dritte, die keine Lizenz besitzen, dürfen die geschützte Vorrichtung nicht herstellen, anbieten, in Verkehr bringen, benutzen oder einführen. Bei Verletzung kann I Unterlassungsansprüche und — bei Verschulden — Schadensersatzansprüche geltend machen (§ 24 GebrMG i.V.m. den Vorschriften des PatG). Der ungeprüfte Charakter hindert die Wirkung nicht — das Gebrauchsmuster ist so lange wirksam, wie es nicht gelöscht wird.",
    sourceIds: ["gebrmg11"],
    sources: [
      { id: "gebrmg11", label: "§ 11 GebrMG", url: "https://www.gesetze-im-internet.de/gebrmg/__11.html" },
    ],
    level: "Anwendbar",
    levelNumber: 3,
    errorCategory: "Rechtsfolge",
    skillTags: ["gebrauchsmuster", "wirkung", "verletzung", "fallkonstellation"],
    reviewStatus: "reviewed",
  },

  {
    id: "da-gebrm-018",
    pathId: "deutsche-ausbildung",
    mode: "ausbildung",
    kind: "single",
    topic: "GebrMG § 23 — Schutzdauer",
    title: "Maximale Schutzdauer des Gebrauchsmusters",
    prompt:
      "Ein Gebrauchsmuster wurde am 1. März 2015 angemeldet und dreimal rechtzeitig verlängert. Wann endet der Schutz spätestens?",
    options: [
      "1. März 2035 (20 Jahre ab Anmeldetag)",
      "1. März 2022 (sieben Jahre, dann kein weiterer Verlängerungsantrag möglich)",
      "1. März 2025 (zehn Jahre ab Anmeldetag — absolute Höchstschutzdauer nach § 23 GebrMG)",
      "1. März 2030 (15 Jahre ab Anmeldetag mit drei Verlängerungen)",
    ],
    answers: [2],
    explanation:
      "§ 23 Abs. 1 GebrMG: Die Schutzdauer beträgt zunächst drei Jahre ab Anmeldetag. Sie kann auf sechs, acht und schließlich zehn Jahre verlängert werden. Die absolute Höchstschutzdauer ist zehn Jahre ab dem Anmeldetag — danach erlischt der Schutz zwingend. Bei Anmeldung am 1. März 2015 endet der Schutz spätestens am 1. März 2025. Dies unterscheidet das Gebrauchsmuster fundamental vom Patent (§ 16 Abs. 1 PatG: 20 Jahre ab Anmeldetag).",
    sourceIds: ["gebrmg23"],
    sources: [
      { id: "gebrmg23", label: "§ 23 GebrMG", url: "https://www.gesetze-im-internet.de/gebrmg/__23.html" },
    ],
    level: "Bekannt",
    levelNumber: 1,
    errorCategory: "Definition",
    skillTags: ["gebrauchsmuster", "schutzdauer"],
    reviewStatus: "reviewed",
  },

  {
    id: "da-gebrm-019",
    pathId: "deutsche-ausbildung",
    mode: "ausbildung",
    kind: "single",
    topic: "GebrMG § 15 — Löschungsantrag",
    title: "Wer kann einen Löschungsantrag stellen?",
    prompt:
      "Wer ist nach § 15 GebrMG berechtigt, einen Löschungsantrag gegen ein Gebrauchsmuster beim DPMA zu stellen?",
    options: [
      "Nur der unmittelbar verletzte Mitbewerber des Gebrauchsmusterinhabers",
      "Jedermann — der Löschungsantrag kann von jeder natürlichen oder juristischen Person gestellt werden",
      "Nur der Anmelder eines kollidierende jüngeren Schutzrechts",
      "Nur das DPMA selbst im Rahmen einer Amtslöschung",
    ],
    answers: [1],
    explanation:
      "§ 15 Abs. 1 GebrMG: Der Antrag auf Löschung eines Gebrauchsmusters kann von jedermann gestellt werden. Das Löschungsverfahren steht damit allen Interessierten offen — es ist nicht auf unmittelbar betroffene Mitbewerber oder Patentinhaber beschränkt. Dies unterscheidet sich vom Einspruchsverfahren beim Patent (§ 59 PatG: ebenfalls jedermann), entspricht aber der Grundkonzeption, dass ungeprüfte Schutzrechte einer breiten Anfechtbarkeit offenstehen müssen. Die DPMA-Amtslöschung ist in § 15 GebrMG nicht geregelt; es gibt jedoch die Möglichkeit der Löschung wegen Schutzrechtsverzichts (§ 20 GebrMG).",
    sourceIds: ["gebrmg15"],
    sources: [
      { id: "gebrmg15", label: "§ 15 GebrMG", url: "https://www.gesetze-im-internet.de/gebrmg/__15.html" },
    ],
    level: "Bekannt",
    levelNumber: 1,
    errorCategory: "Definition",
    skillTags: ["gebrauchsmuster", "loeschung", "verfahren"],
    reviewStatus: "reviewed",
  },

  {
    id: "da-gebrm-020",
    pathId: "deutsche-ausbildung",
    mode: "ausbildung",
    kind: "single",
    topic: "GebrMG § 15 — Löschungsgründe: Fallkonstellation",
    title: "Fall: Löschungsantrag wegen fehlender Schutzfähigkeit",
    prompt:
      "Das Gebrauchsmuster GM 123 schützt eine Sortiervorrichtung für Briefsendungen. Konkurrent K behauptet, eine identische Vorrichtung sei bereits vor der Anmeldung von GM 123 in einem deutschen Fachblatt beschrieben worden. Auf welchen Löschungsgrund kann K sich berufen?",
    options: [
      "§ 15 Abs. 1 Nr. 1 GebrMG — fehlende Schutzfähigkeit (keine Neuheit), da die schriftliche Vorveröffentlichung im Inland zum Stand der Technik nach § 3 Abs. 1 GebrMG gehört",
      "§ 15 Abs. 1 Nr. 3 GebrMG — unzulässige Erweiterung des Schutzbereichs",
      "§ 16 GebrMG — Verfahrensmangel bei der Eintragung",
      "§ 20 GebrMG — Verzicht des Inhabers",
    ],
    answers: [0],
    explanation:
      "§ 15 Abs. 1 Nr. 1 GebrMG: Das Gebrauchsmuster ist auf Antrag zu löschen, wenn der Gegenstand nach §§ 1–3 GebrMG nicht schutzfähig ist. Eine vor dem Anmeldetag erschienene Veröffentlichung in einem deutschen Fachblatt ist als schriftliche Beschreibung nach § 3 Abs. 1 S. 1 GebrMG neuheitsschädlich (gilt weltweit für schriftliche Beschreibungen). Die Löschung wegen fehlender Neuheit ist der klassische Anwendungsfall von § 15 Abs. 1 Nr. 1 GebrMG. Option B (unzulässige Erweiterung) wäre anzuführen, wenn der Schutzbereich des Gebrauchsmusters über die ursprünglich eingereichte Anmeldung hinausgeht.",
    sourceIds: ["gebrmg15", "gebrmg3"],
    sources: [
      { id: "gebrmg15", label: "§ 15 GebrMG", url: "https://www.gesetze-im-internet.de/gebrmg/__15.html" },
      { id: "gebrmg3", label: "§ 3 GebrMG", url: "https://www.gesetze-im-internet.de/gebrmg/__3.html" },
    ],
    level: "Anwendbar",
    levelNumber: 3,
    errorCategory: "Subsumtion",
    skillTags: ["gebrauchsmuster", "loeschung", "neuheit", "fallkonstellation"],
    // FIX angewendet (Orchestrator): Distraktor-Zitat unzulässige Erweiterung = § 15 Abs. 1 Nr. 3 GebrMG (Nr. 2 = älteres Recht/Doppelschutz). Keyed-Answer A unverändert korrekt.
    reviewStatus: "reviewed",
  },

  {
    id: "da-gebrm-021",
    pathId: "deutsche-ausbildung",
    mode: "ausbildung",
    kind: "single",
    topic: "Abgrenzung Gebrauchsmuster / Patent — Prüfung",
    title: "Unterschiede Gebrauchsmuster vs. Patent: Prüfungstiefe",
    prompt:
      "Welche Aussage beschreibt den zentralen Unterschied zwischen Patent und Gebrauchsmuster hinsichtlich der Prüfung durch das DPMA korrekt?",
    options: [
      "Beide Schutzrechte werden vom DPMA materiell auf Neuheit und erfinderischen Schritt geprüft.",
      "Das Patent wird auf Antrag materiell geprüft (Neuheit, erfinderische Tätigkeit, gewerbliche Anwendbarkeit); das Gebrauchsmuster wird nur formell und auf Schutzausschlüsse geprüft — keine materielle Prüfung auf Neuheit/erfinderischen Schritt.",
      "Das Gebrauchsmuster wird materiell geprüft; das Patent wird ohne materielle Prüfung erteilt.",
      "Beide Schutzrechte werden ohne materielle Prüfung eingetragen.",
    ],
    answers: [1],
    explanation:
      "§§ 44, 45 PatG: Das Patent wird auf Antrag des Prüfers oder des Anmelders einer materiellen Prüfung auf Neuheit (§ 3 PatG), erfinderische Tätigkeit (§ 4 PatG) und gewerbliche Anwendbarkeit (§ 5 PatG) unterzogen. § 8 GebrMG: Das Gebrauchsmuster wird dagegen nur formell geprüft — auf formale Anforderungen und auf absolute Ausschlussgründe (§§ 1 Abs. 2, 3, 2 GebrMG). Neuheit und erfinderischer Schritt werden beim Gebrauchsmuster erst im Löschungsverfahren (§§ 15–17 GebrMG) materiell geprüft. Das macht das Gebrauchsmuster schneller und günstiger, aber schutzrechtlich unsicherer.",
    sourceIds: ["gebrmg8"],
    sources: [
      { id: "gebrmg8", label: "§ 8 GebrMG", url: "https://www.gesetze-im-internet.de/gebrmg/__8.html" },
    ],
    level: "Abrufbar",
    levelNumber: 2,
    errorCategory: "Abgrenzung",
    skillTags: ["gebrauchsmuster", "patent", "abgrenzung", "dpma-pruefung"],
    reviewStatus: "reviewed",
  },

  {
    id: "da-gebrm-022",
    pathId: "deutsche-ausbildung",
    mode: "ausbildung",
    kind: "single",
    topic: "Abgrenzung Gebrauchsmuster / Patent — Verfahrensschutz: Fallkonstellation",
    title: "Fall: Chemisches Syntheseverfahren — Patent oder Gebrauchsmuster?",
    prompt:
      "Chemiker C hat ein neuartiges Syntheseverfahren für einen Wirkstoff entwickelt. Er möchte möglichst schnell Schutz erlangen und erwägt ein Gebrauchsmuster. Ist das möglich?",
    options: [
      "Ja — Syntheseverfahren sind als technische Lehre gebrauchsmusterfähig.",
      "Nein — Verfahren sind nach § 2 Nr. 3 GebrMG vom Gebrauchsmusterschutz ausgeschlossen; C muss eine Patentanmeldung einreichen.",
      "Ja — wenn das Verfahren zu einem neuen Erzeugnis führt, erstreckt sich der Gebrauchsmusterschutz automatisch auf das Verfahren.",
      "Nein — chemische Verfahren sind nach § 2 GebrMG wegen des Stoffschutzes gesondert ausgeschlossen.",
    ],
    answers: [1],
    explanation:
      "§ 2 Nr. 3 GebrMG: Vom Gebrauchsmusterschutz ausgeschlossen sind Verfahren aller Art. Das Syntheseverfahren ist ein Verfahren im Sinne dieser Norm — es ist nicht gebrauchsmusterfähig. C muss für das Verfahren eine Patentanmeldung beim DPMA (oder beim EPA) einreichen. Sofern C das durch das Verfahren hergestellte Erzeugnis (den Wirkstoff selbst) schützen möchte und dieser neu und erfinderisch ist, könnte er hierfür ein Gebrauchsmuster und/oder ein Patent anmelden. Aber: Das Verfahren bleibt stets vom Gebrauchsmusterschutz ausgeschlossen.",
    sourceIds: ["gebrmg2"],
    sources: [
      { id: "gebrmg2", label: "§ 2 GebrMG", url: "https://www.gesetze-im-internet.de/gebrmg/__2.html" },
    ],
    level: "Anwendbar",
    levelNumber: 3,
    errorCategory: "Subsumtion",
    skillTags: ["gebrauchsmuster", "ausschluesse", "verfahrensausschluss", "fallkonstellation"],
    reviewStatus: "reviewed",
  },

  {
    id: "da-gebrm-023",
    pathId: "deutsche-ausbildung",
    mode: "ausbildung",
    kind: "single",
    topic: "GebrMG § 5 — Abzweigung: Fallkonstellation",
    title: "Fall: Abzweigung bei drohender Patentversagung",
    prompt:
      "A hat am 5. Mai 2020 eine Patentanmeldung eingereicht. Im April 2025 kündigt der Prüfer an, das Patent wegen fehlender erfinderischer Tätigkeit zu versagen. Kann A noch eine Abzweigung nach § 5 GebrMG vornehmen?",
    options: [
      "Nein — nach einer Versagungsankündigung ist eine Abzweigung ausgeschlossen.",
      "Ja — solange die Patentanmeldung beim DPMA noch anhängig ist, kann A eine Gebrauchsmusteranmeldung abzweigen und dabei den Zeitrang vom 5. Mai 2020 beanspruchen.",
      "Ja — aber die abgezweigte Gebrauchsmusteranmeldung erhält nur den Anmeldetag der Abzweigung.",
      "Nein — eine Abzweigung ist nur möglich, wenn das Patent bereits erteilt wurde.",
    ],
    answers: [1],
    explanation:
      "§ 5 Abs. 1 GebrMG: Die Abzweigung ist möglich, solange die Patentanmeldung beim DPMA anhängig ist — und zwar unabhängig davon, ob eine Versagungsankündigung vorliegt. Im April 2025 ist die Anmeldung noch anhängig, sodass A die Abzweigung noch rechtzeitig erklären kann. Die abgezweigte Gebrauchsmusteranmeldung erhält den Zeitrang der ursprünglichen Patentanmeldung (5. Mai 2020). Strategischer Hintergrund: Auch wenn die erfinderische Tätigkeit (Patentstandard) fehlt, kann der erfinderische Schritt nach § 1 GebrMG dennoch vorliegen, weil der Stand der Technik im GebrMG enger gefasst ist.",
    sourceIds: ["gebrmg5"],
    sources: [
      { id: "gebrmg5", label: "§ 5 GebrMG", url: "https://www.gesetze-im-internet.de/gebrmg/__5.html" },
    ],
    level: "Anwendbar",
    levelNumber: 3,
    errorCategory: "Subsumtion",
    skillTags: ["gebrauchsmuster", "abzweigung", "fallkonstellation"],
    reviewStatus: "reviewed",
  },

  {
    id: "da-gebrm-024",
    pathId: "deutsche-ausbildung",
    mode: "ausbildung",
    kind: "multi",
    topic: "GebrMG — Abgrenzung Patent/Gebrauchsmuster: mehrere Merkmale",
    title: "Unterschiede Patent vs. Gebrauchsmuster (Mehrfachauswahl)",
    prompt:
      "Welche der folgenden Aussagen beschreiben korrekte Unterschiede zwischen Patent und Gebrauchsmuster? (Mehrere Antworten möglich.)",
    options: [
      "Das Gebrauchsmuster schützt keine Verfahren (§ 2 GebrMG), das Patent hingegen schützt auch Verfahren (§ 1 PatG).",
      "Die maximale Schutzdauer des Gebrauchsmusters beträgt 10 Jahre (§ 23 GebrMG), die des Patents 20 Jahre (§ 16 PatG).",
      "Das Patent kennt keine Neuheitsschonfrist für eigene Vorveröffentlichungen, das Gebrauchsmuster hingegen schon (§ 3 Abs. 1 S. 3 GebrMG: 6 Monate).",
      "Das Gebrauchsmuster wird vom DPMA genauso materiell geprüft wie das Patent.",
    ],
    answers: [0, 1, 2],
    explanation:
      "Korrekte Unterschiede: (A) § 2 Nr. 3 GebrMG schließt Verfahren aus; § 1 Abs. 1 PatG schützt auch Verfahren. (B) § 23 GebrMG: max. 10 Jahre; § 16 Abs. 1 PatG: 20 Jahre — jeweils ab Anmeldetag. (C) § 3 Abs. 1 S. 3 GebrMG: 6-monatige Neuheitsschonfrist für eigene Offenbarungen; § 3 PatG kennt diese Ausnahme grundsätzlich nicht (im deutschen Recht). Falsch: (D) Das Gebrauchsmuster ist ein ungeprüftes Schutzrecht — § 8 GebrMG: keine materielle Prüfung auf Neuheit/erfinderischen Schritt.",
    sourceIds: ["gebrmg2", "gebrmg3", "gebrmg8", "gebrmg23"],
    sources: [
      { id: "gebrmg2", label: "§ 2 GebrMG", url: "https://www.gesetze-im-internet.de/gebrmg/__2.html" },
      { id: "gebrmg3", label: "§ 3 GebrMG", url: "https://www.gesetze-im-internet.de/gebrmg/__3.html" },
      { id: "gebrmg8", label: "§ 8 GebrMG", url: "https://www.gesetze-im-internet.de/gebrmg/__8.html" },
      { id: "gebrmg23", label: "§ 23 GebrMG", url: "https://www.gesetze-im-internet.de/gebrmg/__23.html" },
    ],
    level: "Anwendbar",
    levelNumber: 3,
    errorCategory: "Abgrenzung",
    skillTags: ["gebrauchsmuster", "patent", "abgrenzung"],
    reviewStatus: "reviewed",
  },

  {
    id: "da-gebrm-025",
    pathId: "deutsche-ausbildung",
    mode: "ausbildung",
    kind: "single",
    topic: "GebrMG § 3 — Neuheit: ausländische Benutzung (Fallkonstellation)",
    title: "Fall: Produktpräsentation in Frankreich — neuheitsschädlich?",
    prompt:
      "Ein Konkurrent hat eine identische Vorrichtung auf der Pariser Fachmesse im April 2024 öffentlich ausgestellt, aber nie schriftlich veröffentlicht. Anmelder A meldet das Gebrauchsmuster im Oktober 2024 beim DPMA an. Ist die Pariser Ausstellung neuheitsschädlich?",
    options: [
      "Ja — offenkundige Benutzungen im EU-Ausland gelten nach § 3 GebrMG als inländische Benutzungen.",
      "Nein — § 3 Abs. 1 S. 2 GebrMG berücksichtigt offenkundige Benutzungen nur im Inland (Deutschland); die Pariser Ausstellung ist nicht neuheitsschädlich.",
      "Ja — weil Frankreich und Deutschland im Europäischen Patentübereinkommen gemeinsam Mitglied sind.",
      "Nein — die Benutzung liegt innerhalb der sechsmonatigen Neuheitsschonfrist.",
    ],
    answers: [1],
    explanation:
      "§ 3 Abs. 1 S. 2 GebrMG: Offenkundige Benutzungen sind nur neuheitsschädlich, wenn sie im Inland (Deutschland) stattgefunden haben. Eine Messe in Paris ist Ausland — die Ausstellung ist daher für die Gebrauchsmuster-Neuheit nicht relevant. Option C ist falsch: Die EPÜ-Mitgliedschaft ändert nichts am Wortlaut des § 3 GebrMG. Option D ist falsch: Die Neuheitsschonfrist (§ 3 Abs. 1 S. 3 GebrMG) gilt nur für eigene Offenbarungen des Anmelders — die Konkurrenzpräsentation durch einen Dritten fällt nicht darunter. Im Gegensatz dazu wäre die Pariser Ausstellung beim Patent (§ 3 Abs. 1 PatG) neuheitsschädlich.",
    sourceIds: ["gebrmg3"],
    sources: [
      { id: "gebrmg3", label: "§ 3 GebrMG", url: "https://www.gesetze-im-internet.de/gebrmg/__3.html" },
    ],
    level: "Anwendbar",
    levelNumber: 3,
    errorCategory: "Subsumtion",
    skillTags: ["gebrauchsmuster", "neuheit", "stand-der-technik", "fallkonstellation"],
    reviewStatus: "reviewed",
  },

  {
    id: "da-gebrm-026",
    pathId: "deutsche-ausbildung",
    mode: "ausbildung",
    kind: "single",
    topic: "GebrMG § 4 — Anmeldung",
    title: "Zuständige Behörde und Mindestinhalt der Gebrauchsmusteranmeldung",
    prompt:
      "Bei welcher Behörde ist die Gebrauchsmusteranmeldung einzureichen, und welche Angaben sind nach § 4 GebrMG mindestens erforderlich?",
    options: [
      "Beim Europäischen Patentamt (EPA); Mindestinhalt: Schutzansprüche, Beschreibung, Zeichnungen",
      "Beim Deutschen Patent- und Markenamt (DPMA); Mindestinhalt: Antrag auf Eintragung, Angaben zum Anmelder, Beschreibung und Schutzansprüche",
      "Beim Bundespatentgericht; Mindestinhalt: Antrag und Schutzansprüche",
      "Beim Bundesministerium der Justiz; Mindestinhalt: Beschreibung der Erfindung",
    ],
    answers: [1],
    explanation:
      "§ 4 Abs. 1 GebrMG: Die Anmeldung ist beim Deutschen Patent- und Markenamt (DPMA) einzureichen. § 4 Abs. 3 GebrMG: Die Anmeldung muss mindestens enthalten: (1) einen Antrag auf Eintragung des Gebrauchsmusters, (2) Angaben, die es erlauben, die Identität des Anmelders festzustellen, und (3) eine Beschreibung der Erfindung sowie Schutzansprüche. Achtung Anmeldetag: Für die bloße Zuerkennung des Anmeldetags genügen nach § 4a GebrMG (parallel § 35 PatG) Angaben zum Anmelder und eine Beschreibung der Erfindung — Schutzansprüche sind dafür noch nicht erforderlich und können nachgereicht werden. Das Europäische Patentamt (EPA) ist für Gebrauchsmuster nicht zuständig — das EPÜ kennt kein europäisches Gebrauchsmuster.",
    sourceIds: ["gebrmg4", "gebrmg4a"],
    sources: [
      { id: "gebrmg4", label: "§ 4 GebrMG", url: "https://www.gesetze-im-internet.de/gebrmg/__4.html" },
      { id: "gebrmg4a", label: "§ 4a GebrMG", url: "https://www.gesetze-im-internet.de/gebrmg/__4a.html" },
    ],
    level: "Bekannt",
    levelNumber: 1,
    errorCategory: "Definition",
    skillTags: ["gebrauchsmuster", "anmeldung", "dpma"],
    // FIX angewendet (Orchestrator): Keyed-Option B um "Beschreibung" ergänzt; Erklärung trennt jetzt Anmeldetag (§ 4a GebrMG, parallel § 35 PatG: Anmelderangaben + Beschreibung, ohne Schutzansprüche) vom Vollinhalt (§ 4). § 4a als Quelle ergänzt.
    reviewStatus: "reviewed",
  },

  {
    id: "da-gebrm-027",
    pathId: "deutsche-ausbildung",
    mode: "ausbildung",
    kind: "single",
    topic: "GebrMG §§ 15, 16 — Löschungsverfahren: Ablauf",
    title: "Fall: Ablauf des Löschungsverfahrens beim DPMA",
    prompt:
      "K stellt beim DPMA einen Löschungsantrag gegen das Gebrauchsmuster GM 456 des Inhabers I. Was muss das DPMA nach § 16 GebrMG als nächstes tun?",
    options: [
      "Das DPMA löscht das Gebrauchsmuster sofort, ohne den Inhaber zu befragen.",
      "Das DPMA teilt dem Inhaber I den Löschungsantrag mit und setzt ihm eine Frist von einem Monat, um zu widersprechen — widerspricht I nicht, wird das Gebrauchsmuster gelöscht.",
      "Das DPMA verweist K zunächst an das Bundespatentgericht.",
      "Das DPMA führt eine materielle Prüfung auf Neuheit durch und löscht nur, wenn die Neuheit klar fehlt.",
    ],
    answers: [1],
    explanation:
      "§ 16 Abs. 1 GebrMG: Wird ein Löschungsantrag gestellt, teilt das DPMA dem Inhaber des Gebrauchsmusters den Antrag mit und fordert ihn auf, innerhalb eines Monats zu erklären, ob er dem Löschungsantrag widerspricht. Widerspricht der Inhaber nicht, so wird das Gebrauchsmuster gelöscht. § 16 Abs. 2 GebrMG: Widerspricht der Inhaber, führt das DPMA ein streitiges Verfahren durch (ähnlich dem Einspruchsverfahren beim Patent), in dem Neuheit und erfinderischer Schritt materiell geprüft werden. Gegen die Entscheidung kann Beschwerde beim Bundespatentgericht eingelegt werden (§ 18 GebrMG; § 17 GebrMG regelt die Kosten).",
    sourceIds: ["gebrmg16"],
    sources: [
      { id: "gebrmg16", label: "§ 16 GebrMG", url: "https://www.gesetze-im-internet.de/gebrmg/__16.html" },
    ],
    level: "Anwendbar",
    levelNumber: 3,
    errorCategory: "Schema",
    skillTags: ["gebrauchsmuster", "loeschung", "verfahren", "fallkonstellation"],
    // FIX angewendet (Orchestrator, WebFetch § 18 GebrMG verifiziert): Beschwerde zum BPatG = § 18 GebrMG (§ 17 = Kosten).
    reviewStatus: "reviewed",
  },

  {
    id: "da-gebrm-028",
    pathId: "deutsche-ausbildung",
    mode: "ausbildung",
    kind: "single",
    topic: "Abgrenzung Gebrauchsmuster / Patent — Neuheitsschonfrist: Fallkonstellation",
    title: "Fall: Vorveröffentlichung — Patent vs. Gebrauchsmuster",
    prompt:
      "Erfinder E veröffentlicht am 1. Februar 2025 einen Fachartikel über seine neue Vorrichtung. Am 1. Juli 2025 möchte er sowohl eine Patentanmeldung als auch ein Gebrauchsmuster anmelden. Für welches Schutzrecht steht die eigene Vorveröffentlichung der Eintragung entgegen?",
    options: [
      "Für beide Schutzrechte — die Vorveröffentlichung ist bei Patent und Gebrauchsmuster gleichermaßen neuheitsschädlich.",
      "Nur für das Patent — beim Gebrauchsmuster greift die sechsmonatige Neuheitsschonfrist nach § 3 Abs. 1 S. 3 GebrMG.",
      "Nur für das Gebrauchsmuster — beim Patent gibt es eine zwölfmonatige Schonfrist.",
      "Für keines der beiden Schutzrechte — eigene Vorveröffentlichungen sind generell unschädlich.",
    ],
    answers: [1],
    explanation:
      "§ 3 Abs. 1 S. 3 GebrMG: Die sechsmonatige Neuheitsschonfrist gilt für eigene Offenbarungen des Anmelders. Der Artikel vom 1. Februar 2025 liegt 5 Monate vor dem Anmeldetag (1. Juli 2025) — die Schonfrist ist gewahrt. Für das Gebrauchsmuster ist die eigene Veröffentlichung damit unschädlich. Für das Patent (§ 3 PatG): Es gibt im deutschen Patentrecht grundsätzlich keine entsprechende Schonfrist für eigene Vorveröffentlichungen — die Publikation vom 1. Februar 2025 würde die Neuheit der Patentanmeldung zerstören. Option C ist falsch: Beim deutschen Patent gibt es keine zwölfmonatige Schonfrist (diese gilt für US-Anmeldungen).",
    sourceIds: ["gebrmg3"],
    sources: [
      { id: "gebrmg3", label: "§ 3 GebrMG", url: "https://www.gesetze-im-internet.de/gebrmg/__3.html" },
    ],
    level: "Anwendbar",
    levelNumber: 3,
    errorCategory: "Abgrenzung",
    skillTags: ["gebrauchsmuster", "neuheit", "neuheitsschonfrist", "abgrenzung", "fallkonstellation"],
    reviewStatus: "reviewed",
  },

  {
    id: "da-gebrm-029",
    pathId: "deutsche-ausbildung",
    mode: "ausbildung",
    kind: "single",
    topic: "GebrMG — Schutzdauer und Verlängerungsschritte (§ 23)",
    title: "Verlängerungsschritte der Gebrauchsmuster-Schutzdauer",
    prompt:
      "In welchen Verlängerungsschritten kann die Schutzdauer eines Gebrauchsmusters nach § 23 GebrMG verlängert werden?",
    options: [
      "Von 3 auf 5 Jahre, dann auf 8 Jahre, dann auf 10 Jahre",
      "Von 3 auf 6 Jahre, dann auf 8 Jahre, dann auf 10 Jahre",
      "Einmalig von 3 auf 10 Jahre",
      "Von 3 auf 5 Jahre, dann auf 10 Jahre",
    ],
    answers: [1],
    explanation:
      "§ 23 Abs. 1 GebrMG: Die Schutzdauer beträgt zunächst drei Jahre ab Anmeldetag. Verlängerungsschritte: erste Verlängerung auf 6 Jahre (um 3 Jahre), zweite Verlängerung auf 8 Jahre (um 2 Jahre), dritte Verlängerung auf 10 Jahre (um 2 Jahre). Die absolute Höchstschutzdauer von 10 Jahren kann nicht überschritten werden. Jede Verlängerung erfordert einen rechtzeitigen Antrag und die Entrichtung der Verlängerungsgebühr.",
    sourceIds: ["gebrmg23"],
    sources: [
      { id: "gebrmg23", label: "§ 23 GebrMG", url: "https://www.gesetze-im-internet.de/gebrmg/__23.html" },
    ],
    level: "Abrufbar",
    levelNumber: 2,
    errorCategory: "Definition",
    skillTags: ["gebrauchsmuster", "schutzdauer", "verlaengerung"],
    reviewStatus: "reviewed",
  },

  {
    id: "da-gebrm-030",
    pathId: "deutsche-ausbildung",
    mode: "ausbildung",
    kind: "single",
    topic: "GebrMG § 11 / § 24 — Wirkung und Verletzungsansprüche",
    title: "Fall: Ansprüche bei Gebrauchsmusterverletzung",
    prompt:
      "Inhaber I eines eingetragenen Gebrauchsmusters bemerkt, dass Dritter D die geschützte Vorrichtung ohne Lizenz in Deutschland herstellt und vertreibt. Welche Ansprüche stehen I nach dem GebrMG zur Verfügung?",
    options: [
      "Nur Löschungsantrag gegen D beim DPMA",
      "Nur Schadensersatz, wenn D vorsätzlich handelt",
      "Unterlassung (§ 11 GebrMG i.V.m. § 139 PatG analog) und bei Verschulden Schadensersatz sowie Auskunft und Vernichtung",
      "Keine Ansprüche — das ungeprüfte Gebrauchsmuster entfaltet keine Verletzungswirkung",
    ],
    answers: [2],
    explanation:
      "§ 11 GebrMG gewährt dem Gebrauchsmusterinhaber das ausschließliche Benutzungsrecht. § 24 GebrMG verweist für die Rechtsfolgen der Verletzung auf die entsprechenden Vorschriften des Patentgesetzes: Unterlassungsanspruch (§ 139 Abs. 1 PatG analog), Schadensersatz bei Verschulden (§ 139 Abs. 2 PatG analog), Auskunftsanspruch (§ 140b PatG analog) sowie Vernichtungsanspruch (§ 140a PatG analog). Der ungeprüfte Charakter des Gebrauchsmusters schließt diese Ansprüche nicht aus — D kann aber dem Verletzungsverfahren durch einen Löschungsantrag begegnen.",
    sourceIds: ["gebrmg11"],
    sources: [
      { id: "gebrmg11", label: "§ 11 GebrMG", url: "https://www.gesetze-im-internet.de/gebrmg/__11.html" },
    ],
    level: "Anwendbar",
    levelNumber: 3,
    errorCategory: "Rechtsfolge",
    skillTags: ["gebrauchsmuster", "verletzung", "ansprueche", "fallkonstellation"],
    // REVIEW (Befund): Ansprüche (Unterlassung, Schadensersatz bei Verschulden, Auskunft,
    // Vernichtung) und Keyed-Answer korrekt. Hinweis (kein Rechtsfehler, daher freigegeben):
    // Diese Ansprüche sind im GebrMG eigenständig kodifiziert (§ 24 Unterlassung/Schadensersatz,
    // § 24a Vernichtung, § 24b Auskunft GebrMG) — die "§ 139/140a/140b PatG analog"-Formel ist
    // didaktische Kurzform, idealerweise auf die GebrMG-Normen umstellen.
    reviewStatus: "reviewed",
  },

  // ─── ORDERING da-gebrm-031 … da-gebrm-034 ───────────────────────────────

  {
    id: "da-gebrm-031",
    pathId: "deutsche-ausbildung",
    mode: "ausbildung",
    kind: "ordering",
    topic: "GebrMG § 5 — Abzweigungs-Prüfungsschema",
    title: "Prüfungsschema: Abzweigung aus einer Patentanmeldung (§ 5 GebrMG)",
    prompt:
      "Bringe die Prüfungsschritte für eine Abzweigung nach § 5 GebrMG in die richtige gutachterliche Reihenfolge.",
    options: [],
    answers: [],
    orderItems: [
      "Liegt eine Patentanmeldung oder ein Patent beim DPMA vor, auf das die Abzweigung gestützt werden soll?",
      "Ist die Patentanmeldung zum Zeitpunkt der Abzweigung noch beim DPMA anhängig (Abzweigung ist nur bei anhängiger Anmeldung möglich)?",
      "Betrifft der Gegenstand der abgezweigten Gebrauchsmusteranmeldung denselben Gegenstand wie die ursprüngliche Patentanmeldung (keine unzulässige Erweiterung)?",
      "Zeitrang: Die abgezweigte Gebrauchsmusteranmeldung erhält den Anmeldetag der ursprünglichen Patentanmeldung (§ 5 Abs. 1 GebrMG).",
    ],
    explanation:
      "Die Prüfung der Abzweigung (§ 5 GebrMG) folgt dieser Struktur: (1) Bestehen einer Patentanmeldung oder eines erteilten Patents als Bezugsanmeldung; (2) Anhängigkeit der Bezugsanmeldung zum Zeitpunkt der Abzweigung — endet die Patentanmeldung (Erteilung, Zurückweisung, Rücknahme), ist eine Abzweigung nicht mehr möglich; (3) Identität des Gegenstands — der abgezweigte Gebrauchsmustergegenstand muss in der ursprünglichen Patentanmeldung offenbart sein; (4) Rechtsfolge Zeitrang — der Anmeldetag der Patentanmeldung gilt als Anmeldetag des abgezweigten Gebrauchsmusters, was prioritätsrechtlich entscheidend ist.",
    sourceIds: ["gebrmg5"],
    sources: [
      { id: "gebrmg5", label: "§ 5 GebrMG", url: "https://www.gesetze-im-internet.de/gebrmg/__5.html" },
    ],
    level: "Anwendbar",
    levelNumber: 3,
    errorCategory: "Schema",
    skillTags: ["gebrauchsmuster", "abzweigung", "pruefungsschema"],
    reviewStatus: "reviewed",
  },

  {
    id: "da-gebrm-032",
    pathId: "deutsche-ausbildung",
    mode: "ausbildung",
    kind: "ordering",
    topic: "GebrMG §§ 15–17 — Löschungsverfahren-Ablauf",
    title: "Ablauf des Löschungsverfahrens beim DPMA (§§ 15–17 GebrMG)",
    prompt:
      "Bringe die Stationen des Gebrauchsmuster-Löschungsverfahrens beim DPMA in die richtige zeitliche Reihenfolge.",
    options: [],
    answers: [],
    orderItems: [
      "Antragsteller stellt Löschungsantrag beim DPMA mit Begründung (§ 15 GebrMG — Löschungsgründe: fehlende Schutzfähigkeit oder unzulässige Erweiterung)",
      "DPMA teilt den Antrag dem Gebrauchsmusterinhaber mit und setzt Monatsfrist zum Widerspruch (§ 16 Abs. 1 GebrMG)",
      "Widerspricht der Inhaber nicht: DPMA löscht das Gebrauchsmuster; widerspricht er: DPMA führt streitiges Verfahren durch (§ 16 Abs. 2 GebrMG)",
      "Gegen die DPMA-Entscheidung: Beschwerde beim Bundespatentgericht möglich (§ 18 GebrMG)",
    ],
    explanation:
      "§§ 15–17 GebrMG: Das Löschungsverfahren beginnt mit dem Antrag (§ 15). Das DPMA benachrichtigt den Inhaber und setzt eine Widerspruchsfrist (§ 16 Abs. 1). Bei Widerspruch des Inhabers findet ein streitiges Verfahren statt, in dem Neuheit und erfinderischer Schritt erstmals materiell geprüft werden (§ 16 Abs. 2). Die Entscheidung des DPMA kann mit der Beschwerde zum Bundespatentgericht angegriffen werden (§ 18 GebrMG; § 17 = Kosten des Löschungsverfahrens); gegen dessen Entscheidung ist Rechtsbeschwerde zum Bundesgerichtshof möglich.",
    sourceIds: ["gebrmg15", "gebrmg16", "gebrmg18"],
    sources: [
      { id: "gebrmg15", label: "§ 15 GebrMG", url: "https://www.gesetze-im-internet.de/gebrmg/__15.html" },
      { id: "gebrmg16", label: "§ 16 GebrMG", url: "https://www.gesetze-im-internet.de/gebrmg/__16.html" },
      { id: "gebrmg18", label: "§ 18 GebrMG", url: "https://www.gesetze-im-internet.de/gebrmg/__18.html" },
    ],
    level: "Anwendbar",
    levelNumber: 3,
    errorCategory: "Schema",
    skillTags: ["gebrauchsmuster", "loeschung", "verfahren", "pruefungsschema"],
    // FIX angewendet (Orchestrator, WebFetch § 18 GebrMG verifiziert): orderItem 4 + Erklärung + Quelle auf § 18 GebrMG (Beschwerde) umgestellt; § 17 = Kosten.
    reviewStatus: "reviewed",
  },

  {
    id: "da-gebrm-033",
    pathId: "deutsche-ausbildung",
    mode: "ausbildung",
    kind: "ordering",
    topic: "GebrMG — Gebrauchsmuster-Anmeldeverfahren beim DPMA",
    title: "Ablauf der Gebrauchsmusteranmeldung beim DPMA (§§ 4, 8 GebrMG)",
    prompt:
      "Bringe die Stationen der Gebrauchsmusteranmeldung bis zur Eintragung in die richtige Reihenfolge.",
    options: [],
    answers: [],
    orderItems: [
      "Einreichung der Anmeldung beim DPMA mit Mindestinhalt: Eintragungsantrag, Anmelderidentifikation, Schutzansprüche und Beschreibung (§ 4 GebrMG) — Anmeldetag entsteht",
      "Formale Prüfung durch das DPMA: Vollständigkeit der Unterlagen und Gebührenentrichtung",
      "Prüfung auf Schutzausschlüsse nach § 1 Abs. 2, 3 und § 2 GebrMG (kein Verfahrensschutz, keine biotechnologischen Ausschlüsse) — keine materielle Prüfung auf Neuheit oder erfinderischen Schritt",
      "Eintragung des Gebrauchsmusters in die Rolle und Veröffentlichung im Patentblatt (§ 8 GebrMG)",
    ],
    explanation:
      "Das Gebrauchsmuster-Anmeldeverfahren ist deutlich schlanker als das Patentprüfungsverfahren: (1) Anmeldung mit Mindestinhalt beim DPMA (§ 4 GebrMG), der Anmeldetag bestimmt den Zeitrang; (2) formale Prüfung auf Vollständigkeit und Gebühr; (3) Prüfung auf absolute Schutzausschlüsse gemäß § 1 Abs. 2, 3 GebrMG (z.B. Entdeckungen, Spielregeln) und § 2 GebrMG (Verfahren, biotechnologische Ausschlüsse) — Neuheit und erfinderischer Schritt werden nicht geprüft; (4) Eintragung und Veröffentlichung. Keine Widerspruchsfrist wie beim Markenrecht — Dritte müssen aktiv Löschungsantrag stellen.",
    sourceIds: ["gebrmg4", "gebrmg8"],
    sources: [
      { id: "gebrmg4", label: "§ 4 GebrMG", url: "https://www.gesetze-im-internet.de/gebrmg/__4.html" },
      { id: "gebrmg8", label: "§ 8 GebrMG", url: "https://www.gesetze-im-internet.de/gebrmg/__8.html" },
    ],
    level: "Abrufbar",
    levelNumber: 2,
    errorCategory: "Schema",
    skillTags: ["gebrauchsmuster", "anmeldung", "dpma-verfahren", "fallkonstellation"],
    // REVIEW (Befund): Reihenfolge (Einreichung → formale Prüfung → Schutzausschluss-Prüfung
    // ohne Neuheit/erf. Schritt → Eintragung) korrekt; das gewertete orderItem-Sequencing ist
    // einwandfrei, daher freigegeben. Hinweis (kein Sequenzfehler): orderItem 1 koppelt
    // Schutzansprüche an die Anmeldetags-Entstehung — für den Anmeldetag selbst genügen
    // Anmelderangaben + Beschreibung (§ 4a GebrMG); Klammerzusatz bei Gelegenheit präzisieren.
    reviewStatus: "reviewed",
  },

  {
    id: "da-gebrm-034",
    pathId: "deutsche-ausbildung",
    mode: "ausbildung",
    kind: "ordering",
    topic: "GebrMG § 1 — Prüfungsschema Schutzfähigkeit",
    title: "Prüfungsschema Schutzfähigkeit eines Gebrauchsmusters",
    prompt:
      "Bringe die Prüfungsschritte zur Beurteilung der Schutzfähigkeit einer Erfindung als Gebrauchsmuster in die gutachterliche Reihenfolge.",
    options: [],
    answers: [],
    orderItems: [
      "Ist der Gegenstand ein Erzeugnis (Vorrichtung, Gegenstand)? — Verfahren sind nach § 2 Nr. 3 GebrMG ausgeschlossen",
      "Schutzausschlüsse nach § 1 Abs. 2, 3 und § 2 GebrMG prüfen: Entdeckungen, Spielregeln, biotechnologische Ausschlüsse etc.",
      "Neuheit nach § 3 GebrMG prüfen: schriftliche Beschreibungen weltweit, offenkundige Benutzungen nur im Inland; Neuheitsschonfrist (§ 3 Abs. 1 S. 3) berücksichtigen",
      "Erfinderischer Schritt nach § 1 Abs. 1 GebrMG: Unterscheidet sich der Gegenstand naheliegend vom Stand der Technik? (kein Patent-Standard der 'erfinderischen Tätigkeit')",
      "Gewerbliche Anwendbarkeit nach § 1 Abs. 1 GebrMG i.V.m. § 5 PatG analog prüfen",
    ],
    explanation:
      "Das Prüfungsschema für die Schutzfähigkeit eines Gebrauchsmusters: (1) Gegenstandsform — nur Erzeugnisse, keine Verfahren (§ 2 Nr. 3 GebrMG); (2) Absolute Ausschlussgründe (§§ 1 Abs. 2, 3, 2 GebrMG); (3) Neuheit nach § 3 GebrMG mit dem gebrauchsmusterspezifischen Stand der Technik (schriftlich weltwei, Benutzung nur inland; eigene Offenbarungen in der 6-Monats-Schonfrist unschädlich); (4) Erfinderischer Schritt nach § 1 Abs. 1 GebrMG — niedrigere Hürde als beim Patent, da Stand der Technik enger; (5) Gewerbliche Anwendbarkeit.",
    sourceIds: ["gebrmg1", "gebrmg2", "gebrmg3"],
    sources: [
      { id: "gebrmg1", label: "§ 1 GebrMG", url: "https://www.gesetze-im-internet.de/gebrmg/__1.html" },
      { id: "gebrmg2", label: "§ 2 GebrMG", url: "https://www.gesetze-im-internet.de/gebrmg/__2.html" },
      { id: "gebrmg3", label: "§ 3 GebrMG", url: "https://www.gesetze-im-internet.de/gebrmg/__3.html" },
    ],
    level: "Anwendbar",
    levelNumber: 3,
    errorCategory: "Schema",
    skillTags: ["gebrauchsmuster", "schutzvoraussetzungen", "pruefungsschema"],
    // REVIEW: Sequenz und Rechtslage korrekt (Erzeugnis → Ausschlüsse → Neuheit § 3 →
    // erfinderischer Schritt § 1, kein Patent-Standard → gewerbl. Anwendbarkeit). Freigegeben.
    // Nur Tippfehler in der Erklärung ("weltwei" → weltweit, "inland" → Inland) bei Gelegenheit.
    reviewStatus: "reviewed",
  },

  // ─── CLOZE da-gebrm-035 … da-gebrm-038 ─────────────────────────────────

  {
    id: "da-gebrm-035",
    pathId: "deutsche-ausbildung",
    mode: "ausbildung",
    kind: "cloze",
    topic: "GebrMG § 23 — Maximale Schutzdauer",
    title: "Cloze: Maximale Schutzdauer des Gebrauchsmusters (§ 23 GebrMG)",
    prompt:
      "Die absolute Höchstschutzdauer eines Gebrauchsmusters beträgt nach § 23 GebrMG ___ Jahre ab dem Anmeldetag.",
    options: ["3", "7", "10", "20"],
    answers: [2],
    explanation:
      "§ 23 Abs. 1 GebrMG: Die maximale Schutzdauer beträgt zehn Jahre ab dem Anmeldetag. Die Schutzdauer beginnt mit drei Jahren und kann über sechs und acht Jahre auf maximal zehn Jahre verlängert werden. Im Vergleich dazu beträgt die Patentschutzdauer nach § 16 Abs. 1 PatG maximal 20 Jahre ab dem Anmeldetag.",
    sourceIds: ["gebrmg23"],
    sources: [
      { id: "gebrmg23", label: "§ 23 GebrMG", url: "https://www.gesetze-im-internet.de/gebrmg/__23.html" },
    ],
    level: "Bekannt",
    levelNumber: 1,
    errorCategory: "Definition",
    skillTags: ["gebrauchsmuster", "schutzdauer"],
    reviewStatus: "reviewed",
  },

  {
    id: "da-gebrm-036",
    pathId: "deutsche-ausbildung",
    mode: "ausbildung",
    kind: "cloze",
    topic: "GebrMG § 3 Abs. 1 S. 3 — Neuheitsschonfrist",
    title: "Cloze: Dauer der Neuheitsschonfrist (§ 3 GebrMG)",
    prompt:
      "Eigene Offenbarungen des Anmelders, die innerhalb von ___ Monaten vor dem Anmeldetag erfolgten, bleiben nach § 3 Abs. 1 S. 3 GebrMG bei der Neuheitsprüfung außer Betracht.",
    options: ["3", "6", "12", "18"],
    answers: [1],
    explanation:
      "§ 3 Abs. 1 S. 3 GebrMG: Beschreibungen oder Benutzungen der Erfindung durch den Anmelder oder seinen Rechtsvorgänger innerhalb von sechs Monaten vor dem Anmeldetag sind nicht neuheitsschädlich. Diese Neuheitsschonfrist von sechs Monaten gilt ausschließlich für eigene Offenbarungen und ermöglicht es, die Erfindung vor der Anmeldung z.B. auf Messen oder in Fachzeitschriften vorzustellen. Das deutsche Patentrecht kennt keine entsprechende Schonfrist.",
    sourceIds: ["gebrmg3"],
    sources: [
      { id: "gebrmg3", label: "§ 3 GebrMG", url: "https://www.gesetze-im-internet.de/gebrmg/__3.html" },
    ],
    level: "Bekannt",
    levelNumber: 1,
    errorCategory: "Definition",
    skillTags: ["gebrauchsmuster", "neuheit", "neuheitsschonfrist"],
    reviewStatus: "reviewed",
  },

  {
    id: "da-gebrm-037",
    pathId: "deutsche-ausbildung",
    mode: "ausbildung",
    kind: "cloze",
    topic: "GebrMG § 3 — Stand der Technik: Benutzungen",
    title: "Cloze: Reichweite offenkundiger Benutzungen im Gebrauchsmusterrecht",
    prompt:
      "Nach § 3 Abs. 1 S. 2 GebrMG sind offenkundige Benutzungen nur dann neuheitsschädlich, wenn sie ___ stattgefunden haben.",
    options: ["weltweit", "im Inland", "innerhalb der EU", "in einem EPÜ-Mitgliedstaat"],
    answers: [1],
    explanation:
      "§ 3 Abs. 1 S. 2 GebrMG: Offenkundige Benutzungen gehören zum Stand der Technik nur, wenn sie im Inland (Deutschland) stattgefunden haben. Dies ist ein zentraler Unterschied zum Patentrecht (§ 3 Abs. 1 PatG), das offenkundige Benutzungen weltweit berücksichtigt. Schriftliche Beschreibungen hingegen sind nach § 3 Abs. 1 S. 1 GebrMG weltweit neuheitsschädlich — unabhängig davon, wo sie veröffentlicht wurden.",
    sourceIds: ["gebrmg3"],
    sources: [
      { id: "gebrmg3", label: "§ 3 GebrMG", url: "https://www.gesetze-im-internet.de/gebrmg/__3.html" },
    ],
    level: "Abrufbar",
    levelNumber: 2,
    errorCategory: "Abgrenzung",
    skillTags: ["gebrauchsmuster", "neuheit", "stand-der-technik"],
    reviewStatus: "reviewed",
  },

  {
    id: "da-gebrm-038",
    pathId: "deutsche-ausbildung",
    mode: "ausbildung",
    kind: "cloze",
    topic: "GebrMG § 2 — Verfahrensausschluss",
    title: "Cloze: Vom Gebrauchsmusterschutz ausgeschlossene Gegenstände (§ 2 GebrMG)",
    prompt:
      "Nach § 2 Nr. 3 GebrMG sind ___ vom Gebrauchsmusterschutz ausgeschlossen, sodass nur Erzeugnisse (Vorrichtungen) geschützt werden können.",
    options: ["Softwareanwendungen", "Verfahren", "Chemische Stoffe", "Biotechnologische Erfindungen"],
    answers: [1],
    explanation:
      "§ 2 Nr. 3 GebrMG: Verfahren aller Art — einschließlich Herstellungsverfahren, Arbeitsverfahren und Verwendungsverfahren — sind vom Gebrauchsmusterschutz ausgeschlossen. Gebrauchsmusterfähig sind nur Erzeugnisse, also körperliche Gegenstände und Vorrichtungen. Chemische Stoffe können als Erzeugnis grundsätzlich gebrauchsmusterfähig sein (soweit keine biotechnologischen Ausschlüsse greifen). Biotechnologische Erfindungen nach § 2a PatG sind separat in § 2 Nr. 2 GebrMG ausgeschlossen. Softwareanwendungen können als solche nach § 1 Abs. 3 Nr. 3 GebrMG ausgeschlossen sein (Pläne, Regeln und Verfahren für gedankliche Tätigkeiten).",
    sourceIds: ["gebrmg2"],
    sources: [
      { id: "gebrmg2", label: "§ 2 GebrMG", url: "https://www.gesetze-im-internet.de/gebrmg/__2.html" },
    ],
    level: "Bekannt",
    levelNumber: 1,
    errorCategory: "Definition",
    skillTags: ["gebrauchsmuster", "ausschluesse", "verfahrensausschluss"],
    reviewStatus: "reviewed",
  },
];
